import { z } from 'zod';
import { defineEndpoint } from './contract.ts';

// Core's own endpoints.

export const HealthResponse = z.object({
  /** The deployed git sha, or "dev". */
  sha: z.string(),
  /** Seconds since the process started. */
  uptime: z.number(),
  /** Event-loop delay over the last minute, in milliseconds. */
  eventLoopDelay: z.object({ p50: z.number(), p99: z.number() }),
  pocketbase: z.enum(['ok', 'error']),
  /**
   * Domain event delivery: how many events the slowest subscriber is behind, and
   * how many events are parked awaiting an admin. Null when it can't be read.
   */
  eventLag: z.object({ behind: z.number().int(), parked: z.number().int() }).nullable(),
  /** The Discord worker: whether a token is configured, and how many workers have their stream open. */
  worker: z.object({ configured: z.boolean(), connected: z.boolean(), sessions: z.number().int() }),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

export const health = defineEndpoint({
  method: 'GET',
  path: '/health',
  permission: 'public',
  response: HealthResponse,
});

export const CurrentUser = z.object({
  /** The core_users record id. */
  id: z.string(),
  discordId: z.string(),
  username: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});
export type CurrentUser = z.infer<typeof CurrentUser>;

export const MeResponse = z.object({
  user: CurrentUser,
  /** The caller's effective permissions, so the UI can hide what they can't use. ['*'] means all. */
  permissions: z.array(z.string()),
  /**
   * When the login stops counting as recent (12 hours after it), or null without a
   * web session. Pages prompt "Log in again to make changes" before a sensitive form
   * is filled in, not after (design D24).
   */
  recentLoginUntil: z.iso.datetime().nullable(),
  /**
   * The enabled modules still in their trial (cutover milestone A), whose pages say their
   * data is replaced when they go live. Empty when the states couldn't be read.
   */
  trials: z.array(z.object({ name: z.string(), title: z.string() })).default([]),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const me = defineEndpoint({
  method: 'GET',
  path: '/me',
  permission: 'authenticated',
  response: MeResponse,
});

export const logout = defineEndpoint({
  method: 'POST',
  path: '/auth/logout',
  permission: 'authenticated',
  response: z.object({ ok: z.literal(true) }),
});

/** Where login failures send the browser: /login?error=<code>. */
export const LoginError = z.enum(['not_member', 'no_role', 'banned', 'discord_unavailable', 'cancelled', 'expired']);
export type LoginError = z.infer<typeof LoginError>;
